import { Box, CardMedia, CircularProgress, IconButton, Popover, Skeleton, TableCell, TableRow } from '@mui/material'
import type fastq from 'fastq'
import {
  type EnhancedDomain,
  type KickPlateBulkOrderItem,
  getHorizontalHoleCountLabel,
  getVerticalHoleCountLabel,
} from 'metalworks-domain'
import { useEffect, useRef, useState } from 'react'

import { PhxIcon } from '@salsita-ai/platform-project-sdk/ui'

import { BulkFinalizationStatus } from './BulkFinalizationStatus'

interface BulkPlateTableRowProps {
  item: EnhancedDomain.$KickPlateBulkOrderItem
  imageUrl: string | null
  queue: fastq.queue<KickPlateBulkOrderItem>
  onEdit: (item: EnhancedDomain.$KickPlateBulkOrderItem) => void
  onDelete: (item: EnhancedDomain.$KickPlateBulkOrderItem) => void
  selected: boolean
}

export const BulkPlateTableRow = (props: BulkPlateTableRowProps) => {
  const { item, imageUrl, queue, onEdit, onDelete, selected } = props

  const cacheKeyRef = useRef<string | null>(null)
  const cacheKey = [
    item.id,
    item.type,
    item.finalizationId,
    item.horizontalLength,
    item.verticalLength,
    item.horizontalHoleCount,
    item.verticalHoleCount,
  ].join('-')

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const open = Boolean(anchorEl)

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handlePopoverClose = () => {
    setAnchorEl(null)
  }

  useEffect(() => {
    if (cacheKeyRef.current !== cacheKey) {
      cacheKeyRef.current = cacheKey
      queue.push(item)
    }
  }, [cacheKey, queue, item])

  return (
    <TableRow key={item.finalizationId} sx={{ backgroundColor: selected ? 'background.default' : 'transparent' }}>
      <TableCell sx={{ position: 'relative', height: 64 }} align="center">
        <Box sx={{ width: '100%', maxWidth: 100, height: 32, display: 'grid', placeItems: 'center', mx: 'auto' }}>
          {imageUrl ? (
            <CardMedia
              image={imageUrl}
              aria-label={item.type}
              sx={{ width: '100%', height: '100%', backgroundSize: 'contain' }}
              onMouseEnter={handlePopoverOpen}
              onMouseLeave={handlePopoverClose}
            />
          ) : imageUrl === null ? (
            <CircularProgress />
          ) : (
            <Skeleton variant="rectangular" sx={{ width: '100%', height: '100%' }} />
          )}
        </Box>
        <Popover
          sx={{ pointerEvents: 'none' }}
          open={open}
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: 'center',
          }}
          onClose={handlePopoverClose}
          disableRestoreFocus
        >
          <CardMedia
            image={imageUrl}
            aria-label={item.type}
            sx={{ width: 320, height: 320, backgroundSize: 'contain' }}
          />
        </Popover>
      </TableCell>
      <TableCell>{item.horizontalLength}mm</TableCell>
      <TableCell>{item.verticalLength}mm</TableCell>
      <TableCell sx={{ textAlign: 'right' }}>{getHorizontalHoleCountLabel(item.horizontalHoleCount)}</TableCell>
      <TableCell sx={{ textAlign: 'right' }}>{getVerticalHoleCountLabel(item.verticalHoleCount)}</TableCell>
      <TableCell sx={{ textAlign: 'right' }}>{item.quantity}</TableCell>
      <TableCell>
        <BulkFinalizationStatus status={item.finalizationStatus} />
      </TableCell>
      <TableCell sx={{ minWidth: '100px', maxWidth: '100px' }}>
        <IconButton
          onClick={() => {
            onEdit(item)
          }}
        >
          <PhxIcon name="PencilSimpleBold" />
        </IconButton>
        <IconButton>
          <PhxIcon
            name="Trash"
            onClick={() => {
              onDelete(item)
            }}
          />
        </IconButton>
      </TableCell>
    </TableRow>
  )
}
